import { useState, useEffect, useCallback, useRef } from 'react';
import { Participant, RemoteParticipant } from 'twilio-video';
import useVideoContext from '../useVideoContext/useVideoContext';
import useParticipants from '../useParticipants/useParticipants';
import { removeIdentityMasking } from '../../utils';

export type QualitySuggestion = {
  id: string;
  severity: 'warning' | 'error';
  title: string;
  message: string;
  action?: string;
  participantIdentity?: string;
  isLocal?: boolean;
};

/**
 * Check if a participant is in chaperoning mode based on their identity.
 * Chaperoning mode participants have '###CP##' in their identity string.
 * These participants should be hidden from other participants.
 */
function isChaperoning(identity: string): boolean {
  return identity.includes('###CP##');
}

function getParticipantName(identity: string): string {
  // Use removeIdentityMasking to properly handle anonymous participants
  // This returns 'Anonymous Participant' for anonymous meetings
  return removeIdentityMasking(identity);
}

function createLocalAlert(level: number): QualitySuggestion {
  if (level <= 1) {
    return {
      id: 'local-veryLowQuality',
      severity: 'error',
      title: 'Your Connection is Very Poor',
      message: 'Your network connection is very unstable. You may experience disconnections.',
      action: 'Consider disabling HD video or turning off your camera to improve audio quality.',
      isLocal: true,
    };
  }
  return {
    id: 'local-lowQuality',
    severity: 'warning',
    title: 'Your Connection is Poor',
    message: 'Your network connection quality is low. Video quality may be affected.',
    action: 'Try moving closer to your Wi-Fi router or switching to a wired connection.',
    isLocal: true,
  };
}

function createRemoteAlert(participant: RemoteParticipant, level: number): QualitySuggestion {
  const name = getParticipantName(participant.identity);
  if (level <= 1) {
    return {
      id: `remote-${participant.sid}-veryLowQuality`,
      severity: 'error',
      title: `${name} has Very Poor Connection`,
      message: `${name}'s network connection is very unstable. They may have trouble seeing or hearing you.`,
      participantIdentity: participant.identity,
      isLocal: false,
    };
  }
  return {
    id: `remote-${participant.sid}-lowQuality`,
    severity: 'warning',
    title: `${name} has Poor Connection`,
    message: `${name}'s network quality is low. Video quality from them may be affected.`,
    participantIdentity: participant.identity,
    isLocal: false,
  };
}

interface ParticipantQualityState {
  [sid: string]: {
    lastLevel: number | null;
    dismissed: boolean;
  };
}

export default function useNetworkQualityAlert() {
  const { room } = useVideoContext();
  const participants = useParticipants();
  const localParticipant = room?.localParticipant;

  const [activeAlerts, setActiveAlerts] = useState<QualitySuggestion[]>([]);
  const qualityStateRef = useRef<ParticipantQualityState>({});
  const dismissedAlertsRef = useRef<Set<string>>(new Set());

  const dismissAlert = useCallback((alertId: string) => {
    dismissedAlertsRef.current.add(alertId);
    setActiveAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  const clearAllAlerts = useCallback(() => {
    setActiveAlerts([]);
  }, []);

  // Monitor local participant
  useEffect(() => {
    if (!localParticipant) return;

    const handleQualityChange = (level: number) => {
      console.log('🔴 Local network quality changed to:', level);

      const localState = qualityStateRef.current['local'] || { lastLevel: null, dismissed: false };

      if (level <= 2) {
        // Poor quality - show alert if not dismissed for this alert type
        const alertId = level <= 1 ? 'local-veryLowQuality' : 'local-lowQuality';
        if (!dismissedAlertsRef.current.has(alertId)) {
          const alert = createLocalAlert(level);
          setActiveAlerts(prev => {
            // Remove any existing local alerts and add new one
            const filtered = prev.filter(a => !a.isLocal);
            return [...filtered, alert];
          });
        }
      } else {
        // Quality recovered - remove local alerts
        setActiveAlerts(prev => prev.filter(a => !a.isLocal));
        // Reset dismissed state for local alerts so they can show again
        dismissedAlertsRef.current.delete('local-veryLowQuality');
        dismissedAlertsRef.current.delete('local-lowQuality');
      }

      qualityStateRef.current['local'] = { lastLevel: level, dismissed: false };
    };

    // Set initial state
    if (localParticipant.networkQualityLevel !== null) {
      handleQualityChange(localParticipant.networkQualityLevel);
    }

    localParticipant.on('networkQualityLevelChanged', handleQualityChange);
    return () => {
      localParticipant.off('networkQualityLevelChanged', handleQualityChange);
    };
  }, [localParticipant]);

  // Monitor remote participants
  useEffect(() => {
    if (!participants.length) return;

    const handlers = new Map<string, (level: number) => void>();

    participants.forEach((participant: RemoteParticipant) => {
      // Skip chaperoning mode participants - they should be hidden from other participants
      // This prevents exposing their network quality issues to other meeting attendees
      if (isChaperoning(participant.identity)) {
        return;
      }

      const handleQualityChange = (level: number) => {
        console.log(`🔵 Remote participant ${getParticipantName(participant.identity)} quality changed to:`, level);

        if (level <= 2) {
          // Poor quality - show alert
          const alertId = level <= 1
            ? `remote-${participant.sid}-veryLowQuality`
            : `remote-${participant.sid}-lowQuality`;

          if (!dismissedAlertsRef.current.has(alertId)) {
            const alert = createRemoteAlert(participant, level);
            setActiveAlerts(prev => {
              // Remove any existing alerts for this participant and add new one
              const filtered = prev.filter(a =>
                !a.id.startsWith(`remote-${participant.sid}`)
              );
              return [...filtered, alert];
            });
          }
        } else {
          // Quality recovered - remove alerts for this participant
          setActiveAlerts(prev =>
            prev.filter(a => !a.id.startsWith(`remote-${participant.sid}`))
          );
          // Reset dismissed state
          dismissedAlertsRef.current.delete(`remote-${participant.sid}-veryLowQuality`);
          dismissedAlertsRef.current.delete(`remote-${participant.sid}-lowQuality`);
        }

        qualityStateRef.current[participant.sid] = { lastLevel: level, dismissed: false };
      };

      handlers.set(participant.sid, handleQualityChange);

      // Set initial state
      if (participant.networkQualityLevel !== null) {
        handleQualityChange(participant.networkQualityLevel);
      }

      participant.on('networkQualityLevelChanged', handleQualityChange);
    });

    return () => {
      participants.forEach((participant: RemoteParticipant) => {
        const handler = handlers.get(participant.sid);
        if (handler) {
          participant.off('networkQualityLevelChanged', handler);
        }
      });
    };
  }, [participants]);

  // Clean up alerts for participants who left
  useEffect(() => {
    const currentSids = new Set(participants.map(p => p.sid));
    setActiveAlerts(prev =>
      prev.filter(alert => {
        if (alert.isLocal) return true;
        // Extract sid from alert id (format: remote-{sid}-qualityType)
        const match = alert.id.match(/^remote-(.+?)-(very)?lowQuality$/);
        if (!match) return true;
        const sid = match[1];
        return currentSids.has(sid);
      })
    );
  }, [participants]);

  // Expose test functions to window for debugging (remove in production)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).testNetworkAlert = {
        triggerLocalPoor: () => {
          const alert = createLocalAlert(2);
          setActiveAlerts(prev => [...prev.filter(a => !a.isLocal), alert]);
          console.log('Test: Triggered local poor quality alert');
        },
        triggerLocalVeryPoor: () => {
          const alert = createLocalAlert(1);
          setActiveAlerts(prev => [...prev.filter(a => !a.isLocal), alert]);
          console.log('Test: Triggered local very poor quality alert');
        },
        triggerRemotePoor: (name: string = 'Test User') => {
          const alert: QualitySuggestion = {
            id: `test-remote-${Date.now()}`,
            severity: 'warning',
            title: `${name} has Poor Connection`,
            message: `${name}'s network quality is low. Video quality from them may be affected.`,
            participantIdentity: name,
            isLocal: false,
          };
          setActiveAlerts(prev => [...prev, alert]);
          console.log('Test: Triggered remote poor quality alert for', name);
        },
        clear: () => {
          setActiveAlerts([]);
          console.log('Test: Cleared all alerts');
        },
      };
      console.log('🧪 Network alert test functions available:');
      console.log('  - testNetworkAlert.triggerLocalPoor()');
      console.log('  - testNetworkAlert.triggerLocalVeryPoor()');
      console.log('  - testNetworkAlert.triggerRemotePoor("Name")');
      console.log('  - testNetworkAlert.clear()');
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).testNetworkAlert;
      }
    };
  }, []);

  return {
    activeAlerts,
    dismissAlert,
    clearAllAlerts,
  };
}
